/**
 * Billing & tier management page.
 */

import React, { useEffect, useState } from 'react';
import { getBillingInfo, getInvoices, getTierOptions } from '../services';
import { BillingInfo, Invoice, TierInfo } from '../types';
import {
  CreditCard,
  ArrowUpCircle,
  CheckCircle2,
  Download,
} from 'lucide-react';

export default function BillingPage() {
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [tiers, setTiers] = useState<TierInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState<string>('');

  useEffect(() => {
    Promise.all([getBillingInfo(), getInvoices(), getTierOptions()])
      .then(([b, i, t]) => {
        setBilling(b);
        setInvoices(i);
        setTiers(t);
        setSelectedTier(b.currentTier);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !billing) return <div className="page-loader">Loading billing…</div>;

  return (
    <div className="billing-page">
      <div className="page-header">
        <div>
          <h1>Billing</h1>
          <p>Manage your subscription, payment method, and invoices</p>
        </div>
      </div>

      {/* ── Current plan ─────────────────────────────────── */}
      <div className="billing-cards">
        <div className="billing-card">
          <div className="billing-card-icon"><ArrowUpCircle size={24} /></div>
          <div>
            <h3>Current Plan</h3>
            <span className="tier-pill lg">{billing.currentTier}</span>
          </div>
          <div className="billing-card-stat">
            <span className="stat-value">${billing.monthlySpend.toFixed(2)}</span>
            <span className="stat-label">This month</span>
          </div>
        </div>

        <div className="billing-card" style={{ opacity: 0.45, pointerEvents: 'none' }}>
          <div className="billing-card-icon"><CreditCard size={24} /></div>
          <div>
            <h3>Payment Method</h3>
            <p>{billing.paymentMethod ? `${billing.paymentMethod.brand} •••• ${billing.paymentMethod.last4}` : 'None'}</p>
            {billing.paymentMethod && <span className="muted">Expires {billing.paymentMethod.expiryMonth}/{billing.paymentMethod.expiryYear}</span>}
          </div>
          <button className="btn btn-ghost btn-sm" disabled>Update</button>
        </div>
      </div>

      {/* ── Tier Comparison ──────────────────────────────── */}
      {/* <h2 className="section-title">Compare Plans</h2>
      <div className="tier-grid">
        {tiers.map((t) => {
          const isCurrent = t.name === billing.currentTier;
          const isDisabled = t.tier === 'pro' || t.tier === 'enterprise';
          const isSelected = t.name === selectedTier;
          return (
            <div
              key={t.name}
              className={[
                'tier-card',
                isCurrent ? 'current' : '',
                isSelected ? 'selected' : '',
                isDisabled ? 'disabled' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => !isDisabled && setSelectedTier(t.name)}
              style={!isDisabled ? { cursor: 'pointer' } : undefined}
            >
              {isCurrent && <div className="current-badge">Current</div>}
              <h3>
                {t.name}
                {isDisabled && <span className="tier-soon-pill">Coming soon</span>}
              </h3>
              <div className="tier-price">
                {t.monthlyPrice === -1 ? 'Custom' : <>${t.monthlyPrice}<span>/mo</span></>}
              </div>
              <ul className="tier-features">
                <li>{t.rateLimit === -1 ? 'Custom' : t.rateLimit.toLocaleString()} req/min</li>
                <li>{t.maxApiKeys === -1 ? 'Unlimited' : t.maxApiKeys} API keys</li>
                {t.features.map((f) => (
                  <li key={f}><CheckCircle2 size={14} /> {f}</li>
                ))}
              </ul>
              {isCurrent && isSelected ? (
                <button className="btn btn-accent btn-block" disabled>Selected</button>
              ) : !isCurrent && !isDisabled ? (
                <button className={`btn btn-outline btn-block${isSelected ? ' btn-accent' : ''}`}>
                  {t.tier === 'enterprise' ? 'Contact Sales' : billing.currentTier === 'free' ? 'Upgrade' : 'Switch'}
                </button>
              ) : null}
            </div>
          );
        })}
      </div> */}

      {/* ── Invoices ─────────────────────────────────────── */}
      <h2 className="section-title">Invoices</h2>
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Description</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Download</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id}>
                <td className="cell-primary" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{inv.id}</td>
                <td>{inv.description}</td>
                <td>{new Date(inv.date).toLocaleDateString()}</td>
                <td>${inv.amount.toFixed(2)}</td>
                <td>
                  <span className={`status-badge ${inv.status}`}>{inv.status}</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  {inv.downloadUrl ? (
                    <button className="btn-icon" title="Download PDF">
                      <Download size={16} />
                    </button>
                  ) : (
                    <span style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>—</span>
                  )}
                </td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr>
                <td colSpan={6} className="empty-state">No invoices yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
